/**
 * Step 4 (Supporting evidence) — private medical records (PMR) self-upload,
 * as a file upload inside the List & Loop (Multiple Responses) pattern.
 *
 * Modeled on v1's additionalDocumentsListLoopPages
 * (all-claims/pages/supportingEvidenceEnhancement/
 * evidenceChoiceAdditionalDocumentsListLoop.js): an array-builder loop with a
 * summary page and a single-item page pairing one `va-file-input` with a
 * decoupled document-type select, per VADS recommendation.
 *
 * Only shown when "I'll upload my records myself" was selected on
 * /supporting-evidence/other-providers-sharing-options.
 */
import React from 'react';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  fileInputUI,
  fileInputSchema,
  selectUI,
  selectSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { MISSING_FILE } from 'platform/forms-system/src/js/validation';
import get from 'platform/utilities/data/get';
import {
  FILE_TYPES,
  HINT_TEXT,
  UPLOAD_URL,
  ADDITIONAL_ATTACHMENT_LABEL,
  PMR_ATTACHMENTS_TYPES,
} from '../../../shared/components/fileInputComponent/constants';
import {
  createPayload,
  parseResponse,
} from '../../../shared/utils/fileInputMultiUIConfig';
import { pmrUploadDescription } from '../../content/privateMedicalRecordsUpload';
import { prepareFilesAccordion } from '../../content/fileUpload';
import {
  listLoopPatternWithBypassedIntro,
  willUploadPrivateRecords,
} from './utils';

/** Value-to-label map for the document-type select and summary cards. */
export const PMR_ATTACHMENT_LABELS = PMR_ATTACHMENTS_TYPES.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }),
  {},
);

/** arrayPath for the PMR self-upload loop. */
export const PMR_UPLOAD_ARRAY_PATH = 'privateMedicalRecordsUploads';

/**
 * Item page path (with :index) — used to deep-link into the loop. Figma
 * (#157626) labels this page /other-providers-records-upload; arrayBuilder
 * requires `/:index` on item pages, so it carries the same `/:index/document`
 * suffix as the additional evidence upload loop.
 */
export const PMR_UPLOAD_ITEM_PATH =
  'supporting-evidence/other-providers-records-upload/:index/document';

/** Loop summary page path. */
export const PMR_UPLOAD_SUMMARY_PATH =
  'supporting-evidence/other-providers-records-summary';

/**
 * Loop descriptor for the skip-intro navigation helpers: entering the loop
 * goes straight to the first item page (add mode) or the loop summary.
 */
export const PMR_UPLOAD_LOOP = {
  arrayPath: PMR_UPLOAD_ARRAY_PATH,
  itemPath: PMR_UPLOAD_ITEM_PATH,
  summaryPath: PMR_UPLOAD_SUMMARY_PATH,
};

/** Decision page ("sharing options") that gates the self-upload loop. */
export const PMR_ACCESS_PATH =
  'supporting-evidence/other-providers-sharing-options';

/**
 * Intro-bypass pattern: the loop's intro page never displays; all routes
 * that would land there go to the access decision page instead.
 */
export const pmrUploadBypassedIntro = listLoopPatternWithBypassedIntro(
  PMR_UPLOAD_LOOP,
  PMR_ACCESS_PATH,
);

/** @type {ArrayBuilderOptions} */
export const arrayBuilderOptions = {
  arrayPath: PMR_UPLOAD_ARRAY_PATH,
  nounSingular: 'private medical record',
  nounPlural: 'private medical records',
  // Optional multi-page pattern (VADS "Multiple responses"): the access page
  // already captures whether the Veteran is uploading records themselves, so
  // the loop itself does not force at least one item.
  required: false,
  isItemIncomplete: item =>
    !item?.uploadedDocument?.confirmationCode || !item?.attachmentId,
  text: {
    getItemName: item =>
      item?.uploadedDocument?.name || 'Private medical record',
    cardDescription: item => PMR_ATTACHMENT_LABELS[item?.attachmentId] || '',
    cancelAddButtonText: () => 'Cancel adding this record',
    cancelAddYes: 'Yes, cancel adding this record',
    cancelAddNo: 'No, continue adding this record',
    deleteTitle: 'Delete file?',
    deleteDescription: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return `We’ll delete this file: ${itemName}`;
    },
    deleteYes: () => 'Delete file',
    deleteNo: () => 'Keep file',
    alertItemDeleted: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return `We’ve deleted file: ${itemName}`;
    },
  },
};

/**
 * Shown in place of the summary cards (e.g. after deleting the last
 * remaining uploaded record). Placed as its own schema field — rather than
 * in the summary page's title/description text overrides — so it renders
 * after the page's alerts/cards block instead of above it.
 */
const NoUploadedRecordsMessage = () => (
  <p>
    There are no uploaded records from other healthcare providers associated
    with this claim.
  </p>
);

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:noUploadedRecordsMessage': {
      'ui:description': NoUploadedRecordsMessage,
      'ui:options': {
        hideIf: formData => !!get(PMR_UPLOAD_ARRAY_PATH, formData)?.length,
      },
    },
    'view:hasAnotherPrivateMedicalRecord': arrayBuilderYesNoUI(
      arrayBuilderOptions,
      {
        title: 'Do you have records from a healthcare provider to add?',
        labels: {
          Y: 'Yes',
          N: 'No',
        },
      },
      {
        title: 'Do you have records from other healthcare providers to add?',
        labels: {
          Y: 'Yes',
          N: 'No',
        },
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:noUploadedRecordsMessage': { type: 'object', properties: {} },
      'view:hasAnotherPrivateMedicalRecord': arrayBuilderYesNoSchema,
    },
    required: ['view:hasAnotherPrivateMedicalRecord'],
  },
};

/**
 * Item page: single file upload plus a document-type select rendered as a
 * separate form control (decoupled from the file card, per the VADS
 * recommendation).
 * @returns {PageSchema}
 */
const uploadPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: 'Upload your records from other healthcare providers',
      description: pmrUploadDescription,
      nounSingular: arrayBuilderOptions.nounSingular,
      hasMultipleItemPages: false,
    }),
    uploadedDocument: {
      ...fileInputUI({
        title: 'Upload records',
        hint: HINT_TEXT,
        required: () => true,
        accept: FILE_TYPES,
        fileUploadUrl: UPLOAD_URL,
        formNumber: '21-526EZ',
        fileSizesByFileType: {
          pdf: {
            maxFileSize: 1024 * 1024 * 100,
            minFileSize: 1024,
          },
          default: {
            maxFileSize: 1024 * 1024 * 50,
            minFileSize: 1,
          },
        },
        errorMessages: { required: MISSING_FILE },
        createPayload,
        parseResponse,
      }),
      'ui:validations': [
        // Needed for validation to work correctly inside arrays; taken from
        // the ownedAssetPages.js precedent.
        // https://va.ghe.com/software/vets-design-system-documentation/issues/4837
        (errors, fieldData) => {
          if (fieldData?.isEncrypted && !fieldData?.confirmationCode) {
            return;
          }
          if (!fieldData || !fieldData.name) {
            errors.addError(MISSING_FILE);
          }
          if (fieldData?.errorMessage) {
            errors.addError(fieldData.errorMessage);
          }
        },
      ],
    },
    attachmentId: selectUI({
      title: ADDITIONAL_ATTACHMENT_LABEL,
      required: () => true,
      labels: PMR_ATTACHMENT_LABELS,
      errorMessages: { required: 'Choose a document type' },
      // explicit aria-describedby text, read together with the select's label.
      messageAriaDescribedby: 'Choose the type of document you uploaded above.',
    }),
  },
  schema: {
    type: 'object',
    required: ['uploadedDocument', 'attachmentId'],
    properties: {
      uploadedDocument: fileInputSchema(),
      attachmentId: selectSchema(Object.keys(PMR_ATTACHMENT_LABELS)),
    },
  },
};

/**
 * Array-builder page set for the PMR self-upload List & Loop flow.
 * Spread into uploadAndSummaryPages in place of the previous single
 * privateMedicalRecordsUploadFile placeholder page.
 */
export const privateMedicalRecordsUploadPages = arrayBuilderPages(
  arrayBuilderOptions,
  pageBuilder => ({
    privateMedicalRecordsUploadSummary: pageBuilder.summaryPage({
      title: 'Review your uploaded records',
      path: 'supporting-evidence/other-providers-records-summary',
      depends: willUploadPrivateRecords,
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
      // Intro-bypass pattern: Back goes to the access decision page.
      ...pmrUploadBypassedIntro.summaryPage,
    }),
    privateMedicalRecordsUploadFile: pageBuilder.itemPage({
      title: 'Upload your records from other healthcare providers',
      path: PMR_UPLOAD_ITEM_PATH,
      depends: willUploadPrivateRecords,
      uiSchema: uploadPage.uiSchema,
      schema: uploadPage.schema,
      // Intro-bypass pattern: Back returns to the loop summary if items
      // remain, otherwise to the access decision page.
      ...pmrUploadBypassedIntro.itemPage,
      // rendered between the cancel button and the nav buttons on the item
      // page (“How to prepare your files” accordion).
      ContentBeforeButtons: () => prepareFilesAccordion,
    }),
  }),
);

export default privateMedicalRecordsUploadPages;
